/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, DataTable, SkeletonDisplayText, Pagination, Icon, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons';
import Filter from 'src/components/containers/analytics-v2/filter';
import PopoverActionButton from 'src/components/views/analytics-v2/popover-action-button';
import { Link } from 'react-router-dom';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { formatNumberByLocale } from 'src/helpers';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import {
  QUERY_EXECUTION_KEY,
  SEARCH_ANALYTIC_METRIC,
  TOP_ITEM_IN_DETAIL,
  NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
  MAP_SORT_VALUE,
  SORT_VALUE,
  FEATURES
} from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  data: any[],
  perPage: number,
  page: number,
  totalPage: number,
  hasNext: boolean,
  hasPrevious: boolean,
  directionSort: string
};

const fromDateStateName = 'searchAnalyticsFromDate';
const toDateStateName = 'searchAnalyticsToDate';
const compareStateName = 'searchAnalyticsCompare';

const orderHeaders = [
  'searchTerm',
  'totalSearch',
  'revenue',
  'orderCount',
  'conversionRate',
  'clickThroughRate'
];

class SearchAnalyticsSearches extends Component<ComposedProps, State> {
  state = {
    data: [],
    perPage: 10,
    page: 1,
    hasPrevious: false,
    hasNext: false,
    totalPage: 0,
    directionSort: 'desc'
  };

  componentDidMount() {
    const { searchTerms, fromDate, toDate, compare, searchTermsLoading, metricPath } = this.props;
    const { perPage } = this.state;

    const hasNext = _.size(searchTerms) > perPage;
    this.setState({ rows: searchTerms, hasNext });

    // Normally, when reload or change date, it call api in componentWillReceiveProps
    // but for the case change date in the other tab, then swith this tab, force it to call api again
    if (
      fromDate &&
      toDate &&
      !searchTermsLoading &&
      (!metricPath || metricPath !== 'search-term-detail')
    ) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getTopSearchTerms(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      searchTerms: nextSearchTerms
    } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      // Reset page when change date filter
      this.setState({
        page: 1
      });

      onAbortApiAnalytic();
      setTimeout(() => {
        this.getTopSearchTerms(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    if (!_.isEmpty(nextSearchTerms)) {
      const newTotalPage = Math.ceil(nextSearchTerms.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL);
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(this.state.page, nextSearchTerms);

    // Update execution ids
    this.processExecutionId_SearchTerms(this.props, nextProps);
  }

  renderSkeleton = (numberOfRow: number, numberOfColumn: number) => {
    return _.range(numberOfRow).map(__ =>
      _.range(numberOfColumn).map(__ => <SkeletonDisplayText size="small" />)
    );
  };

  paginateData = (page, data) => {
    this.setState({
      data: data.slice(
        (page - 1) * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
        page * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      )
    });
  };

  buildOptions = () => {
    const { searchTermsLoading, tenantApp } = this.props;
    const { data } = this.state;
    const result = [];
    if (searchTermsLoading) {
      [...Array(5)].forEach(() =>
        result.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ])
      );
    } else {
      data.forEach(item => {
        const { searchTerm, conversionRate, totalSearch, revenue, orderCount, clickThroughRate } =
          item;
        result.push([
          <div>
            <Link
              className="BC-Link__Dashboard"
              key={'subMenu-' + searchTerm}
              to={`/search-analytics/searches/${encodeURIComponent(searchTerm)}`}>
              {searchTerm}
            </Link>
          </div>,
          <div className="cell-value-table">{formatNumberByLocale(totalSearch)}</div>,
          <div className="cell-value-table">{formatNumberByLocale(revenue)}</div>,
          <div className="cell-value-table">{formatNumberByLocale(orderCount)}</div>,
          <div className="cell-value-table">{conversionRate}%</div>,
          <div className="cell-value-table">{clickThroughRate}%</div>,
          <PopoverActionButton searchTerm={searchTerm} tenantApp={tenantApp} />
        ]);
      });
    }
    return result;
  };

  handleSort = (headingIndex: number, direction: string) => {
    const { sortData } = this.props;
    this.setState({
      directionSort: MAP_SORT_VALUE[direction]
    });
    sortData(
      'topSearchTerms',
      orderHeaders[headingIndex],
      MAP_SORT_VALUE[direction],
      headingIndex === 0 ? 'searchTerm' : ''
    );
  };

  nextPage = () => {
    const { page, totalPage } = this.state;
    const { searchTerms } = this.props;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, searchTerms);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page } = this.state;
    const { searchTerms } = this.props;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, searchTerms);
    this.setState({
      page: page - 1
    });
  };

  getTopSearchTerms = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.SEARCH_TERMS;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchTerms(fromDate, toDate, compare, queryExecutionId, TOP_ITEM_IN_DETAIL);
  };

  processExecutionId_SearchTerms = (props, nextProps) => {
    const { searchTerms_QueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      // compare: nextCompare, // TODO: remove this line when there's any update later
      searchTerms_QueryExecutionId: searchTerms_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (
      !_.isEmpty(searchTerms_QueryExecutionIdNext) &&
      searchTerms_QueryExecutionId !== searchTerms_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCH_TERMS,
        executionId: searchTerms_QueryExecutionIdNext
      });
    }
  };

  render() {
    const { i18n, openReviewAddonModal, performActionSurvey, searchTerms, searchTermsLoading } =
      this.props;
    const { page, directionSort, totalPage } = this.state;
    const options = this.buildOptions();
    const arrowSort = directionSort === SORT_VALUE.ASCENDING ? CaretUpMinor : CaretDownMinor;

    return (
      <div className="BC-Page BC-Page__SearchAnalytics__SearchTerms">
        <Filter
          feature={FEATURES.SEARCH}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={SEARCH_ANALYTIC_METRIC.SEARCH_TERMS}
          showCompare={false}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Card
            title={
              <Tooltip
                preferredPosition="above"
                content={i18n.translate('AdminHTML.Analytics.Common.tooltip_TableSearches')}>
                <span className="Polaris-Heading">
                  {i18n.translate('AdminHTML.Analytics.SearchAnalytics.searches_cardTitle')}
                </span>
              </Tooltip>
            }
            sectioned>
            <DataTable
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
              sortable={[true, true, true, true, true, true]}
              onSort={this.handleSort}
              defaultSortDirection="descending"
              initialSortColumnIndex={1}
              footerContent={
                _.isEmpty(searchTerms) && !searchTermsLoading
                  ? i18n.translate('AdminHTML.Analytics.SearchAnalytics.searchesTerms_noResult', {
                      count: 0
                    })
                  : ''
              }
              headings={[
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_searchTermHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_totalSearchHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_revenueHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_orderCountHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_conversionRateHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.searchesTerms_clickThroughRateHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                i18n.translate('AdminHTML.Analytics.SearchAnalytics.searchesTerms_actionHeader')
              ]}
              rows={options}
            />
          </Card>
          {!_.isEmpty(searchTerms) && !searchTermsLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
              <Pagination
                label={`${page}/${totalPage}`}
                hasPrevious={page > 1}
                onPrevious={this.previousPage}
                hasNext={page < totalPage}
                onNext={this.nextPage}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getTopSearchTerms: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getListTopSearchTerms(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  sortData: (name, field, direction, fieldInsensitive = '') =>
    dispatch(AnalyticsActions.sortData(name, field, direction, fieldInsensitive)),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    tenantApp: _.get(state, 'oauth.tenantApp', null),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    metricPath: _.get(state.analyticsV2, 'pathname', false),
    searchTerms: _.get(state.analyticsV2, 'topSearchTerms', []),
    searchTermsLoading: _.get(state.analyticsV2, 'topSearchTermsLoading', false),
    searchTermsSuccess: _.get(state.analyticsV2, 'topSearchTermsSuccess', false),
    searchTerms_QueryExecutionId: _.get(state.analyticsV2, 'topSearchTermsQueryExecutionId', ''),
    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', [])
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SearchAnalyticsSearches));
